import { SpecialProjectOutput } from "@serializers/special-projects"
import { makeObservable, observable, action, computed } from "mobx"
import { addDays } from "date-fns"
import { getCookie, setCookies } from "cookies-next"
import { Context, Languages } from "@providers"
import { push } from "@lib/history"
import { parse } from "query-string"
import { isServer } from "@lib/isomorphic"
import { htmlToTextTransform } from "@lib/html"
import { FEATURE_TOGGLES_PRESET } from "@serializers/feature-toggles"
import { SMART_BANNER_PRESET } from "@serializers/smart-banner"
import { COOKIES_KEYS } from "@lib/cookies"
import { config } from "@lib/config"

import { BaseModel } from "./base.model"

import type { NextPageContext } from "next"
import type { MouseEventHandler } from "react"
import type { LanguageISO, Redirect, PressLink } from "@providers"
import type { ConfigData } from "@lib/config"
import type { FeatureTogglesSerializerOutput } from "@serializers/feature-toggles"
import type { Localization } from "@serializers/localization"
import type { DocumentSerializerOutput } from "@serializers/documents"
import type { SmartBannerSerializerOutput } from "@serializers/smart-banner"
import type { BranchLinksGlobal } from "@serializers/branch-links"

const BRANCH_LINKS_DEFAULT: BranchLinksGlobal = Object.freeze({
  ru: {},
  en: {},
  fr: {},
  de: {},
  zh: {},
  es: {},
})

const PRESS_LINK_DEFAULT: PressLink = {
  enabled: false,
  url: "https://press.pure.app/",
}

// This is the language read when the current one has no site-wide templates, so
// that menus stay readable instead of rendering empty labels.
const DEFAULT_TEMPLATES_LANG = "en"

export class GlobalModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable lang = "ru" as LanguageISO
  @observable accessLanguages = [] as LanguageISO[]
  @observable config!: ConfigData
  @observable
  featureToggles = FEATURE_TOGGLES_PRESET as FeatureTogglesSerializerOutput

  @observable pages = {
    aboutUs: {
      enabled: false,
    },
    faq: {
      enabled: false,
    },
    terms: {
      enabled: false,
    },
  }
  @observable templates = [] as Localization[]
  @observable isMenuOpened = false
  @observable htmlEmbeddedList = {} as { [uniqId: string]: string }
  @observable firstServerUrl = ""
  @observable originUrl = ""

  @observable hasAnyOneOfPureAppRuHTTPHeader = false

  @observable redirects = [] as Redirect[]

  @observable pressLink = PRESS_LINK_DEFAULT as PressLink
  @observable AFLinkBase = "https://pureapp.onelink.me/M5p9"
  @observable appGalleryLink = "https://appgallery.huawei.com/app/C104872513"

  @observable terms = [] as DocumentSerializerOutput[]
  @observable privacy = [] as DocumentSerializerOutput[]
  @observable security = [] as DocumentSerializerOutput[]
  @observable guidelines = [] as DocumentSerializerOutput[]
  @observable smartBanner = SMART_BANNER_PRESET as SmartBannerSerializerOutput

  @observable isSmartBannerOpened = true
  @observable isSmartBannerDisabled = false

  @observable branchLinks: BranchLinksGlobal | {} = BRANCH_LINKS_DEFAULT
  @observable specialProjects: SpecialProjectOutput[] = []

  @observable countryCode = ""

  @computed get isPureAppRu() {
    if (isServer()) {
      return (
        this.firstServerUrl.includes(PURE_APP_RU_DOMAIN) ||
        this.hasAnyOneOfPureAppRuHTTPHeader
      )
    }

    return (
      window.location.host.includes(PURE_APP_RU_DOMAIN) ||
      this.hasAnyOneOfPureAppRuHTTPHeader
    )
  }

  @computed get langShortname() {
    const key = Object.keys(Languages).find((key) => key === this.lang) ?? "en"
    // @ts-ignore
    return Languages[key].shortname
  }

  @computed get currentUrl() {
    if (isServer()) return this.firstServerUrl
    else
      return this.generateSiteUrl({
        protocol: window.location.protocol,
        host: window.location.host,
        path: window.location.pathname,
      })
  }

  @computed get specProjectsByLang() {
    return this.specialProjects.filter(({ lang }) => lang === this.lang)
  }

  @computed get branch_links() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this.branchLinks[this.lang] || BRANCH_LINKS_DEFAULT
  }

  getSpecialProjects = async () => {
    const specialProjects = await this.Providers.GlobalProvider.getSpecialProjects()

    this.applyData({ specialProjects })
  }

  openMenu: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    this.applyData({ isMenuOpened: true })
  }

  closeMenu = () => {
    this.applyData({ isMenuOpened: false })
  }

  initSmartBanner = (ctx?: NextPageContext) => {
    try {
      if (ctx) {
        const search = ctx.req?.url?.split("?").slice(-1)[0] || ""
        const utmSource = parse(search).utm_source
        const fromApp = utmSource === "inapp"
        let smartBannerDisabledCookie = false

        if (fromApp) {
          this.setSmartBannerDisabledCookie(ctx)
        } else {
          smartBannerDisabledCookie = Boolean(
            getCookie(COOKIES_KEYS["pure.journal.smart_banner.disabled"], {
              req: ctx.req,
            }),
          )
        }

        this.applyData({
          isSmartBannerDisabled: smartBannerDisabledCookie || fromApp,
        })
      }
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error("Init smart banner error", e)
    }
  }

  setIsSmartBannerDisabled = (isDisabled: boolean) => {
    this.applyData({ isSmartBannerDisabled: isDisabled })
  }

  closeSmartBanner = () => {
    this.applyData({ isSmartBannerOpened: false })
  }

  setSmartBannerDisabledCookie = (ctx?: NextPageContext) => {
    setCookies(COOKIES_KEYS["pure.journal.smart_banner.disabled"], 1, {
      expires: addDays(
        new Date(),
        this.featureToggles.smartBanner?.redisplayIntervalJournal || 30,
      ),
      req: ctx?.req,
      res: ctx?.res,
    })
  }

  getSecurity = async () => {
    try {
      const security = await this.Providers.GlobalProvider.getSecurity(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ security })
    } catch {}
  }

  getGuidelines = async () => {
    try {
      const guidelines = await this.Providers.GlobalProvider.getGuideline(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ guidelines })
    } catch {}
  }

  getSmartBanner = async () => {
    try {
      const smartBanner = await this.Providers.GlobalProvider.getSmartBanner(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ smartBanner })
    } catch {}
  }

  doHeadersContainDomain = (ctx: NextPageContext, domain: string) => {
    return (
      ctx.req?.headers.origin?.includes(domain) ||
      ctx.req?.headers.host?.includes(domain) ||
      ctx.req?.headers[":authority:"]?.includes(domain) ||
      ctx.req?.headers["X-Pure-Origin"]?.includes(domain) ||
      ctx.req?.headers["x-pure-origin"]?.includes(domain)
    )
  }

  setFirstServerUrl = action((ctx: NextPageContext) => {
    const protocol = "https:"
    const host = ctx.req?.headers.host ?? ""
    const path = `${this.config.prefix}${ctx.req?.url ?? ""}`
    this.originUrl = this.generateSiteUrl({
      protocol,
      host,
      path: this.config.prefix,
    })
    this.firstServerUrl = this.generateSiteUrl({ protocol, host, path })
    const hasAnyOneOfPureAppRuHTTPHeader = this.doHeadersContainDomain(
      ctx,
      PURE_APP_RU_DOMAIN,
    )
    this.applyData({
      hasAnyOneOfPureAppRuHTTPHeader,
    })
  })

  setPostFirstServerUrl = ({
    slug,
    lang,
    scope = "journal",
    isAmp,
  }: {
    slug: string
    lang: string
    scope?: string
    isAmp: boolean
  }) => {
    const protocol = "https:"
    const host = config.data.host
    const prefix = config.data.prefix
    const path = `${prefix || ""}/${lang}/${scope}/${slug}`
    const search = isAmp ? "?amp=1" : ""

    this.applyData({
      firstServerUrl: this.generateSiteUrl({ protocol, host, path, search }),
    })

    console.log("FIRST SERVER URL", isServer(), this.firstServerUrl) // eslint-disable-line
  }

  generateSiteUrl = ({
    protocol,
    host,
    path,
    search = "",
  }: {
    protocol: string
    host: string
    path: string
    search?: string
  }) => {
    return `${protocol}//${host}${path}${search}`
  }

  getInitial = async () => {
    const {
      languages,
      pages,
      templates,
      redirects,
      featureToggles,
      specialProjects,
      branchLinks,
      pressLink,
      AFLinkBase,
      appGalleryLink,
    } = await this.Providers.GlobalProvider.getGlobal()
    this.applyData({
      accessLanguages: languages,
      pages,
      templates,
      redirects,
      featureToggles,
      specialProjects,
      branchLinks,
      pressLink: pressLink || PRESS_LINK_DEFAULT,
      AFLinkBase: AFLinkBase ?? this.AFLinkBase,
      appGalleryLink: appGalleryLink ?? this.appGalleryLink,
    })
  }

  setLang = async (lang: LanguageISO, force?: boolean) => {
    if (
      !force &&
      (this.accessLanguages.findIndex((accessLang) => accessLang === lang) ===
        -1 ||
        typeof Languages[lang] === "undefined")
    ) {
      return
    }

    this.applyData({ lang })
  }

  setConfig = action((config: ConfigData) => {
    this.config = config
  })

  changeLang = async (lang: LanguageISO) => {
    let currentUri = `${window.location.pathname}${window.location.search}`
    currentUri = currentUri.replace(
      new RegExp(`^${this.config.prefix}`, "ig"),
      "",
    )

    Object.values(Languages).forEach((lang) => {
      currentUri = currentUri.replace(new RegExp(`/${lang.id}$`, "ig"), "")
      currentUri = currentUri.replace(new RegExp(`/${lang.id}/`, "ig"), "")
    })

    await push({
      href: `/${lang}${
        currentUri.substring(0, 1) === "/" || currentUri === ""
          ? currentUri
          : `/${currentUri}`
      }`,
    })
  }

  getTemplate = (key: string, simpleText = true) => {
    // English as the fallback, so that a page shown in a language the site-wide
    // templates do not cover keeps a readable menu. Without it every label
    // returns an empty string and the footer renders invisible links. Pages
    // that force their own language, such as Safer Dating and /ua/guidelines,
    // are the ones that need it.
    // @ts-ignore
    const currentTemplates =
      // @ts-ignore
      this.templates[this.lang] || this.templates[DEFAULT_TEMPLATES_LANG] || []

    const find = currentTemplates.find((template) => template.key === key)
    if (!find) return ""

    return simpleText ? htmlToTextTransform(find.template) : find.template
  }

  setHtmlEmbedded = action((uniqId: string, source: string) => {
    this.htmlEmbeddedList[uniqId] = source
  })

  getClientCountryCode = async (ctx: Context) => {
    if (this.countryCode) {
      return this.countryCode
    }
    const countryCode = await this.Providers.GlobalProvider.getClientCountryCode(
      ctx,
    )
    this.applyData({
      countryCode,
    })
    return countryCode
  }

  setClientCountryCode = (countryCode: string) => {
    if (this.countryCode) {
      return
    }

    this.applyData({ countryCode })
  }

  hydrate = (data?: Partial<ReturnType<GlobalModel["serialize"]>>) => {
    this.applyData({
      lang: data?.lang,
      accessLanguages: data?.accessLanguages,
      config: data?.config,
      featureToggles: data?.featureToggles,
      smartBanner: data?.smartBanner,
      pages: data?.pages,
      firstServerUrl: data?.firstServerUrl,
      originUrl: data?.originUrl,
      templates: data?.templates,
      branchLinks: data?.branchLinks,
      terms: data?.terms,
      privacy: data?.privacy,
      security: data?.security,
      guidelines: data?.guidelines,
      isSmartBannerOpened: data?.isSmartBannerOpened,
      isSmartBannerDisabled: data?.isSmartBannerDisabled,
      specialProjects: data?.specialProjects,
      countryCode: data?.countryCode,
      pressLink: data?.pressLink,
      AFLinkBase: data?.AFLinkBase,
      appGalleryLink: data?.appGalleryLink,
    })
  }

  serialize = () => {
    return {
      lang: this.lang,
      accessLanguages: this.accessLanguages,
      config: this.config,
      featureToggles: this.featureToggles,
      pages: this.pages,
      firstServerUrl: this.firstServerUrl,
      originUrl: this.originUrl,
      templates: this.templates,
      branchLinks: this.branchLinks,
      terms: this.terms,
      privacy: this.privacy,
      security: this.security,
      guidelines: this.guidelines,
      smartBanner: this.smartBanner,
      isSmartBannerOpened: this.isSmartBannerOpened,
      isSmartBannerDisabled: this.isSmartBannerDisabled,
      specialProjects: this.specialProjects,
      countryCode: this.countryCode,
      pressLink: this.pressLink,
      AFLinkBase: this.AFLinkBase,
      appGalleryLink: this.appGalleryLink,
    }
  }
}

export const PURE_APP_DOMAIN = "pure.app"
export const PURE_APP_RU_DOMAIN = "pure-app.ru"
